import { useEffect, useRef, useState } from 'react';

import { Check, CheckCircle2, CircleMinus, CircleX, Copy } from 'lucide-react';

import { Button } from 'components/Button';
import { Flex } from 'components/layout/Flex';
import { Typography } from 'components/typography';
import {
  CloneCreateResponse,
  CloneJobItem,
  CloneJobItemReason,
  CloneJobItemStatus,
  CloneJobStatus,
} from 'lib/api/cloneLoads';
import { Maybe } from 'types/UtilityTypes';
import { copyToClipboard } from 'utils/copyToClipboard';

import { itemMessage, itemResultLabel, shouldShowItemMessage } from './utils';

type JobDetailsProps = {
  job: CloneCreateResponse;
  onOpenLoad: (loadIdentifier: string) => void;
};

export default function JobDetails({ job, onOpenLoad }: JobDetailsProps) {
  return (
    <div className='border-t border-neutral-200'>
      {job.items.length > 0 && (
        <div className='divide-y divide-neutral-200'>
          {job.items.map((item, index) => (
            <JobItemRow
              key={`${item.reference || 'unknown'}-${index}`}
              item={item}
              onOpenLoad={onOpenLoad}
            />
          ))}
        </div>
      )}

      {job.status === CloneJobStatus.Failed && job.message && (
        <div className='border-t border-neutral-200 px-3 py-2.5 first:border-t-0'>
          <Typography
            variant='body-xs'
            weight='medium'
            className='text-error-700'
          >
            Job error
          </Typography>
          <Typography
            variant='body-xs'
            textColor='lessMuted'
            className='mt-0.5 leading-4'
          >
            {job.message}
          </Typography>
        </div>
      )}

      {job.extractionWarnings.length > 0 && (
        <div className='border-t border-neutral-200 px-3 py-2.5 first:border-t-0'>
          <Typography
            variant='body-xs'
            weight='medium'
            className='text-warning-800'
          >
            Extraction notes
          </Typography>
          <ul className='mt-1 space-y-1'>
            {job.extractionWarnings.map((warning, index) => (
              <Typography
                key={`${warning}-${index}`}
                as='li'
                variant='body-xs'
                textColor='lessMuted'
                className='leading-4'
              >
                {warning}
              </Typography>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

type JobItemRowProps = {
  item: CloneJobItem;
  onOpenLoad: (loadIdentifier: string) => void;
};

function JobItemRow({ item, onOpenLoad }: JobItemRowProps) {
  const reference = item.reference.trim() || 'Unknown reference';
  const resultLabel = itemResultLabel(item);
  const isExisting =
    item.status === CloneJobItemStatus.Skipped &&
    item.reason === CloneJobItemReason.DuplicateExisting;
  const loadIdentifier =
    item.load?.freightTrackingId?.trim() || item.load?.externalTmsId?.trim();
  const loadLabel = isExisting
    ? `Existing load ${loadIdentifier || reference}`
    : loadIdentifier;
  const [copied, setCopied] = useState(false);
  const copyResetTimer =
    useRef<Maybe<ReturnType<typeof globalThis.setTimeout>>>(null);

  useEffect(
    () => () => {
      if (copyResetTimer.current) {
        globalThis.clearTimeout(copyResetTimer.current);
      }
    },
    []
  );

  const copyLoadIdentifier = async () => {
    if (!loadIdentifier) {
      return;
    }
    const didCopy = await copyToClipboard(loadIdentifier, {
      showToast: false,
    });
    if (!didCopy) {
      return;
    }

    setCopied(true);
    if (copyResetTimer.current) {
      globalThis.clearTimeout(copyResetTimer.current);
    }
    copyResetTimer.current = globalThis.setTimeout(
      () => setCopied(false),
      1500
    );
  };

  return (
    <Flex align='start' className='group gap-2.5 px-3 py-2.5'>
      <ItemStatusIcon status={item.status} />
      <div className='min-w-0 flex-1'>
        <Typography
          variant='body-xs'
          weight='medium'
          className='break-words leading-4'
        >
          {reference}
        </Typography>
        {(item.status !== CloneJobItemStatus.Skipped || !itemMessage(item)) && (
          <Typography
            variant='body-xs'
            textColor='lessMuted'
            className='mt-0.5 leading-4'
          >
            {resultLabel}
          </Typography>
        )}
        {shouldShowItemMessage(item) && (
          <Typography
            variant='caption'
            className='mt-0.5 break-words text-[11px] leading-4'
          >
            {itemMessage(item)}
          </Typography>
        )}
        {isExisting && !loadIdentifier && (
          <Typography variant='body-xs' textColor='muted' className='mt-1'>
            {loadLabel}
          </Typography>
        )}
        {loadIdentifier && (
          <Flex align='center' className='mt-1 gap-0.5'>
            <Button
              buttonNamePosthog={null}
              variant='unstyled'
              size='auto'
              className='rounded-sm px-1 py-0.5 text-xs font-medium text-brand-main underline-offset-2 transition-colors duration-150 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-main motion-reduce:transition-none'
              onClick={() => onOpenLoad(loadIdentifier)}
              aria-label={
                isExisting
                  ? `Open ${loadLabel} in Drumkit`
                  : `Open ${loadIdentifier} in Drumkit`
              }
            >
              {loadLabel}
            </Button>
            <Button
              buttonNamePosthog={null}
              variant='unstyled'
              size='auto'
              className='h-6 w-6 rounded-sm text-neutral-500 opacity-0 transition-[color,opacity,transform] duration-150 hover:text-neutral-800 active:scale-[0.97] focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-main group-hover:opacity-100 group-focus-within:opacity-100 motion-reduce:transition-none'
              onClick={() => void copyLoadIdentifier()}
              aria-label={
                copied ? `Copied ${loadIdentifier}` : `Copy ${loadIdentifier}`
              }
            >
              {copied ? (
                <Check
                  className='h-3.5 w-3.5 text-success-600'
                  aria-hidden='true'
                />
              ) : (
                <Copy className='h-3.5 w-3.5' aria-hidden='true' />
              )}
            </Button>
          </Flex>
        )}
      </div>
    </Flex>
  );
}

function ItemStatusIcon({ status }: { status: CloneJobItemStatus }) {
  if (status === CloneJobItemStatus.Created) {
    return (
      <CheckCircle2
        className='mt-0.5 h-3.5 w-3.5 shrink-0 text-success-600'
        aria-hidden='true'
      />
    );
  }
  if (status === CloneJobItemStatus.Skipped) {
    return (
      <CircleMinus
        className='mt-0.5 h-3.5 w-3.5 shrink-0 text-neutral-500'
        aria-hidden='true'
      />
    );
  }
  return (
    <CircleX
      className='mt-0.5 h-3.5 w-3.5 shrink-0 text-error-600'
      aria-hidden='true'
    />
  );
}
